import React, { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Company, User } from 'src/constants/types';
import { useSelectedApplicationSettingsService } from 'src/modules/applications/services';
import { setCurrentCompanyId, getCurrentCompanies } from 'src/modules/companies/slices';
import { useUserCompaniesService, useAdminCompanyService } from 'src/modules/companies/services';
import {
  useGetPermissionsService,
  useGetProfileService,
  useGetRolesService,
} from 'src/modules/profile/services';
import { useCampaignConfigLoader } from 'src/modules/campaignConfigBuilder/hooks/useCampaignConfigLoader';
import { useCampaignCreateOptionsLoader } from 'src/modules/campaignCreateOptions/hooks';
import { fetchSignupResumeLookup, isSignupStepPath } from 'src/modules/signup/helpers';
import { generateUrlWithCompany } from 'src/helpers/helpers';
import { isBhiAdmin } from 'src/helpers/permissions';
import { clearAuthTokens, getAccessToken } from 'src/api/axiosClient/tokenStorage';
import { logSafeAxiosError } from 'src/api/axiosClient/errorHandling';
import { identifyAnalyticsUser } from 'src/services/analytics';

export interface AuthContextType {
  isReady: boolean;
  isAuthenticated: boolean | null;
  /**
   * Startup could not find out whether this company belongs in the funnel. The app must render nothing of
   * itself while this is set: see `resolveStartupPlacement` for why the question cannot be left open.
   */
  signupStateUnavailable: boolean;
  retryStartup: () => void;
  login: () => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

// Onboarding has reached a state of its own, so nothing about the funnel is left to ask.
const SettledOnboardingStatuses = ['failed', 'processing', 'completed'];

/**
 * Where a signed-in client belongs before the app renders: their own funnel step, the onboarding wizard, or
 * where they already are.
 *
 * `unavailable` is the third answer, and it is not `stay`. A company created by the funnel looks exactly like
 * one whose onboarding was never touched, so with the funnel lookup unanswered there is no way to tell an
 * unfinished funnel from an ordinary new account — and treating that as `stay` lets the client into the app,
 * where `PublicRoute` sends them from `/signup` to `/home` and nothing asks again until the next page load. A
 * timeout would quietly drop a client out of the funnel they were paying their way through.
 *
 * It belongs to that lookup alone. It is reached only for a company whose onboarding is known to be
 * unsettled, which is what keeps a held app to the few clients the ambiguity is real for.
 */
export type StartupPlacement =
  | { kind: 'stay' }
  | { kind: 'move'; route: string }
  | { kind: 'unavailable' };

export const resolveStartupPlacement = async(input: {
  companyId: string;
  getOnboardingStatus: (companyId: string) => Promise<{ onboardingStatus: string }>;
}): Promise<StartupPlacement> => {
  let status: { onboardingStatus: string };
  try {
    status = await input.getOnboardingStatus(input.companyId);
  } catch (error) {
    logSafeAxiosError(error);
    // Best-effort, as it was before the funnel existed: this call only decides whether to offer the wizard,
    // and every admin makes it on every page load, so holding the app on it took down everyone whose company
    // is merely half set up, self-service or not.
    return { kind: 'stay' };
  }

  if (SettledOnboardingStatuses.includes(status.onboardingStatus)) return { kind: 'stay' };

  // The funnel decides before the wizard does: sent to /onboarding, a client would be asked for the card the
  // funnel is about to ask for itself.
  const resume = await fetchSignupResumeLookup(input.companyId);
  if (resume.kind == 'unavailable') return { kind: 'unavailable' };

  return { kind: 'move', route: resume.kind == 'resume' ? resume.route : '/onboarding' };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const companiesFromSlice = useSelector(getCurrentCompanies);

  const { getApplicationSettingsById, getApplicationSettingsByDomain } =
    useSelectedApplicationSettingsService();
  const { getCompanies } = useUserCompaniesService();
  const { getOnboardingStatus } = useAdminCompanyService();
  const getProfile = useGetProfileService();
  const getRoles = useGetRolesService();
  const getPermissions = useGetPermissionsService();
  const { loadCampaignConfigs } = useCampaignConfigLoader();
  const { loadCreateOptions } = useCampaignCreateOptionsLoader();

  const [appState, setAppState] = useState<{
    isReady: boolean;
    isAuthenticated: boolean | null;
    signupStateUnavailable: boolean;
  }>({
    isReady: false,
    isAuthenticated: null,
    signupStateUnavailable: false,
  });

  const init = useCallback(async () => {
    const accessToken = getAccessToken();
    
    if (!accessToken) {
      try {
        await getApplicationSettingsByDomain();
        setAppState({ isReady: true, isAuthenticated: false, signupStateUnavailable: false });
      } catch (e) {
        logSafeAxiosError(e);
        console.log('Your application is currently unavailable.');
      }
      return;
    }

    try {
      const companies = companiesFromSlice.length > 0 
        ? companiesFromSlice 
        : await getCompanies();
      const company = getCompany(companies);
      if (company) {
        const [,profile,,] = await Promise.all([
          getApplicationSettingsById(company.applicationId),
          getProfile(company.id),
          getPermissions(company.id),
          getRoles(),
        ]);

        const userProfile = profile as User | undefined;

        dispatch(setCurrentCompanyId(company.id));

        if (userProfile) {
          identifyAnalyticsUser(userProfile, company);
        }

        const shouldCheckOnboarding =
          userProfile &&
          (isBhiAdmin(userProfile) || userProfile.role?.name === 'company_admin') &&
          !window.location.pathname.includes('/onboarding') &&
          !window.location.pathname.includes('/admin/') &&
          // A client standing on a funnel step is already where they belong. Only those screens are
          // excluded, not every path under /signup: the plan screen has to be able to send a client with an
          // unfinished funnel back into it, which is the whole point of the check below.
          !isSignupStepPath(window.location.pathname);

        if (shouldCheckOnboarding) {
          // Twice before giving up, and with no wait in between: the second call never runs on the ordinary
          // path, and it absorbs the single dropped request that is most of what `unavailable` ever means.
          let placement = await resolveStartupPlacement({
            companyId: company.id,
            getOnboardingStatus: getOnboardingStatus,
          });
          if (placement.kind == 'unavailable') {
            placement = await resolveStartupPlacement({
              companyId: company.id,
              getOnboardingStatus: getOnboardingStatus,
            });
          }

          if (placement.kind == 'move') {
            window.location.href = generateUrlWithCompany(placement.route, company.id);
            return;
          }
          if (placement.kind == 'unavailable') {
            // Held rather than let through. `isReady` is set so this pass does not repeat on its own: the
            // client presses `retryStartup`, and the app renders nothing of itself until they do.
            setAppState({ isReady: true, isAuthenticated: true, signupStateUnavailable: true });
            return;
          }
        }

        // Independent bulk fetches — run them concurrently. Pass the company's
        // applicationId explicitly: it isn't in the store yet on this first
        // synchronous pass, so relying on the selector would fetch with a
        // stale/undefined applicationId and cache the wrong result.
        await Promise.all([
          loadCampaignConfigs(false, company.id),
          loadCreateOptions(false, company.id, company.applicationId),
        ]);
      } else {
        await getApplicationSettingsByDomain();
      }

      setAppState({ isReady: true, isAuthenticated: true, signupStateUnavailable: false });
    } catch (e) {
      clearAuthTokens();

      try {
        await getApplicationSettingsByDomain();

        setAppState({ isReady: true, isAuthenticated: false, signupStateUnavailable: false });
      } catch (e) {
        logSafeAxiosError(e);
        console.log('Your application is currently unavailable.');
      }
    }
  }, [
    companiesFromSlice,
    getApplicationSettingsByDomain,
    getCompanies,
    getApplicationSettingsById,
    getProfile,
    getPermissions,
    getRoles,
    dispatch,
    getOnboardingStatus,
    loadCampaignConfigs,
    loadCreateOptions,
  ]);

  useEffect(() => {
    if (!appState.isReady) {
      init();
    }
  }, [appState.isReady]);

  return (
    <AuthContext.Provider
      value={{
        isReady: appState.isReady,
        isAuthenticated: appState.isAuthenticated,
        signupStateUnavailable: appState.signupStateUnavailable,
        retryStartup: () => {
          setAppState({ isReady: false, isAuthenticated: null, signupStateUnavailable: false });
        },
        login: () => {
          setAppState({ isReady: false, isAuthenticated: true, signupStateUnavailable: false });
        },
        logout: () => {
          setAppState({ isReady: false, isAuthenticated: false, signupStateUnavailable: false });
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function getCompany(companies: Company[]): Company | null {
  const urlParams = new URLSearchParams(window.location.search);
  const urlCompanyId = urlParams.get('companyId');
  const companyIdToUse = urlCompanyId || localStorage.getItem('bhiCurrentCompany');

  if (companyIdToUse) {
    const selectedCompanyFromId = companies.find(c => c.id === companyIdToUse);
    if (selectedCompanyFromId) {
      localStorage.setItem('bhiCurrentCompany', selectedCompanyFromId.id);
      return selectedCompanyFromId;
    }
  }

  const selectedCompanyFromList = companies[0];
  if (selectedCompanyFromList) {
    localStorage.setItem('bhiCurrentCompany', selectedCompanyFromList.id);
    return selectedCompanyFromList;
  }

  localStorage.removeItem('bhiCurrentCompany');
  return null;
}
